import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DxTreeListComponent } from 'devextreme-angular';
import { SearchForm } from '../../Model/Dto/SearchForm';
import { UrlResponse } from '../../Model/Dto/UrlResponse';
import { CatalogService } from '../../Services/CatalogManagement/catalog.service';
import { LoginService } from '../../Services/login.service';
import { SystemService } from '../../Services/system.service';

@Component({
  selector: 'np-catalog-tree',
  templateUrl: './catalog-tree.component.html',
  styleUrls: ['./catalog-tree.component.css'],
  standalone: false
})
export class CatalogTreeComponent implements OnInit, OnChanges {

  @ViewChild(DxTreeListComponent) treeList: DxTreeListComponent;

  @Input() catalogId: string;
  @Input() customerId: string;
  @Input() additionalFilter: string;
  @Input() isTarget = false;

  @Input() searchText: string = '';
  @Output() searchTextChange = new EventEmitter<string>();

  @Input() selectionMode: string = 'single';
  @Input() selectionRecursive: boolean = true;
  @Input() selectionAllowSelectAll: boolean = true;
  @Input() selectedRowKeys: any[] = [];
  @Output() selectedRowKeysChange = new EventEmitter<any[]>();

  @Input() showProductStateOptions = false;
  @Input() showSearchFormTab = true;
  @Input() isTargetFilter = false;
  @Input() stateValue: string[] = [];
  @Input() refreshWithSearchForm: SearchForm;
  @Input() syncSearchFormWithService = true;

  @Input() showContextMenu = false;
  @Input() menuItems: any[] = [];

  @Input() dragGroup: string = null;
  @Input() allowDropInsideItem = false;
  @Input() allowReordering = false;
  @Input() showDragIcons = false;
  @Input() onDragStartFn: Function;
  @Input() onDropFn: Function;

  @Input() showLanguageSelector = false;
  @Input() languageFlags: any[];
  @Input() selectedTranslateLanguage: string;
  @Output() selectedTranslateLanguageChange = new EventEmitter<string>();

  @Input() hasSpidFn: (spid: string) => boolean = null;

  @Input() catalogList: any[];

  @Output() onSelectionChanged = new EventEmitter<any>();
  @Output() onRowClick = new EventEmitter<any>();
  @Output() onContentReady = new EventEmitter<any>();
  @Output() onInitialized = new EventEmitter<any>();

  @Output() onContextMenuShowing = new EventEmitter<{ event: any; item: any }>();
  @Output() onContextMenuItemClick = new EventEmitter<{ event: any; item: any }>();

  @Output() onStateChanged = new EventEmitter<{ mode: string; states: string[] }>();
  @Output() onSearchFieldsChanged = new EventEmitter<any>();
  @Output() onEditSearchForm = new EventEmitter<SearchForm>();

  activeSearchFormFilter: SearchForm = null;

  showFilter = false;
  activeFilterTabIndex = 0;
  filterTabs: { text: string }[] = [];
  dataSource: any;
  private lazyDataSource: any;
  private searchTimeout: any;

  constructor(
    public translate: TranslateService,
    private catalogService: CatalogService,
    private loginService: LoginService,
    private systemService: SystemService
  ) {}

  ngOnInit(): void {
    this.buildFilterTabs();
    if (this.catalogId) {
      this.initDataSource();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['catalogId'] && !changes['catalogId'].firstChange) {
      this.initDataSource();
    }
    if (changes['additionalFilter'] && !changes['additionalFilter'].firstChange) {
      this.applyAdditionalFilter();
    }
  }

  private async applyAdditionalFilter(): Promise<void> {
    if (this.additionalFilter) {
      const filterString = ['parent;=;' + this.catalogId];
      const states = [this.additionalFilter];

      const allData = await this.catalogService.getChilds(
        this.catalogId,
        '',
        this.customerId,
        filterString,
        states,
        this.getProductSearchOptions(),
        this.getSelectedStatesMode()
      );

      this.dataSource = allData;
    } else {
      this.dataSource = this.lazyDataSource;
    }
  }

  private initDataSource(): void {
    if (!this.catalogId) {
      this.dataSource = null;
      this.lazyDataSource = null;
      return;
    }

    const self = this;

    this.lazyDataSource = {
      load: async function (loadOptions) {
        if (!self.catalogId) {
          return null;
        }

        let parentId = '';
        if (loadOptions.parentIds != null) {
          const filtered = loadOptions.parentIds.filter((p) => p != undefined);
          parentId = filtered.join(',');
        }

        const states = [...self.getSelectedStates()];
        if (self.additionalFilter) {
          states.push(self.additionalFilter);
        }

        const childs = await self.catalogService.getChilds(
          self.catalogId,
          parentId,
          self.customerId,
          loadOptions.filter,
          states,
          self.getProductSearchOptions(),
          self.getSelectedStatesMode()
        );

        const products = childs.filter((x) => x.type === 'pro');
        if (products && products.length > 249 && self.hasActiveSearch(loadOptions.filter)) {
          self.systemService.notifyInfo(
            self.translate.instant('Das Suchergebnis wurde auf 250 Produkten eingeschränkt')
          );
        }

        return childs;
      }
    };

    this.dataSource = this.lazyDataSource;
  }

  private getSelectedStates(): string[] {
    return this.isTarget
      ? this.catalogService.selectedStatesTarget
      : this.catalogService.selectedStates;
  }

  private getSelectedStatesMode(): string {
    return this.isTarget
      ? this.catalogService.selectedStatesTargetMode
      : this.catalogService.selectedStatesMode;
  }

  private getProductSearchOptions() {
    return this.isTarget
      ? this.loginService.productSearchOptionsTarget
      : this.loginService.productSearchOptions;
  }

  private hasActiveSearch(filter: any): boolean {
    if (this.getSelectedStates()?.length > 0) return true;
    if (filter && filter.filterValue) return true;
    if (this.searchText) return true;
    return false;
  }

  onSearchInputInternal(e: any): void {
    if (e?.event?.currentTarget) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.treeList?.instance?.searchByText(e.event.currentTarget.value);
      }, 500);
    }
  }
  
  async updateState(event: { mode: string; states: string[] }): Promise<void> {
    const { mode, states } = event;

    if (this.isTarget) {
      this.catalogService.selectedStatesTargetMode = mode;
      this.catalogService.selectedStatesTarget = states;
    } else {
      this.catalogService.selectedStatesMode = mode;
      this.catalogService.selectedStates = states;
    }

    if (states && states.length > 0) {
      const filterString = ['parent;=;' + this.catalogId];

      const allData = await this.catalogService.getChilds(
        this.catalogId,
        '',
        this.customerId,
        filterString,
        states,
        this.getProductSearchOptions(),
        mode
      );

      const products = allData.filter((x) => x.type === 'pro');
      if (products && products.length > 249) {
        this.systemService.notifyInfo(
          this.translate.instant('Das Suchergebnis wurde auf 250 Produkten eingeschränkt')
        );
      }

      this.dataSource = allData;
    } else {
      this.dataSource = this.lazyDataSource;
    }

    this.onStateChanged.emit(event);
  }

  updateSearchFields(event: any): void {
    this.onSearchFieldsChanged.emit(event);
  }

  async executeSearchFormInternal(searchForm: SearchForm) {
    this.activeSearchFormFilter = searchForm ?? null;
    if(searchForm == null)
    { 
      this.dataSource = this.lazyDataSource;
      return; 
    }

    const data = await this.catalogService.searchBySearchForm(
      this.catalogService.selectedCatalogId,
      this.loginService.currentUser.customerId,
      searchForm.id,
      searchForm.filterCondition,
      searchForm.searchOptions.map(criteria => ({
        systemKey: criteria.nextPimField.systemKey,
        elementKey: criteria.nextPimField.elementKey,
        field: criteria.nextPimField.field,
        operator: criteria.operator,
        value: criteria.value,
        valueTo: criteria.valueTo,
        values: criteria.values
      }))
    );

    this.dataSource = data;
  }

  onCellPreparedInternal(e: any): void {
    if (!e.data) return;

    if (!e.data.imageUrl) {
      const catalog = this.findCatalog();

      if (catalog && catalog.lastImageUpload > e.data.lastImageLookup) {
        this.catalogService
          .getThumbUrl(e.data.id, e.data.type, this.customerId, this.catalogId)
          .subscribe((url: UrlResponse) => {
            if (url) e.data.imageUrl = url.url;
            e.data.lastImageLookup = new Date().getTime();
          });
      }
    }
  }

  private findCatalog(): any {
    // Try catalogList input first (for combineCatalogs with multiple catalog lists)
    if (this.catalogList?.length) {
      return this.catalogList.find(c => c.id === this.catalogId);
    }
    // Fallback: use catalogService.catalog (for browse)
    if (this.catalogService.catalog?.id === this.catalogId) {
      return this.catalogService.catalog;
    }
    return null;
  }


  refresh(): void {
    this.treeList?.instance?.refresh();
  }

  collapseAll(): void {
    const instance = this.treeList?.instance;
    if (!instance) return;
    const rows = instance.getVisibleRows();
    rows.forEach(row => {
      if (row.isExpanded) {
        instance.collapseRow(row.key);
      }
    });
  }

  async expandAndSelect(treeElementId: string): Promise<void> {
    await this.treeList?.instance?.selectRows([treeElementId], false);
  }

  // --- Filter tabs ---

  private buildFilterTabs(): void {
    this.filterTabs = [];
    this.filterTabs.push({ text: this.translate.instant('Filter') });
    if (this.showSearchFormTab) {
      this.filterTabs.push({ text: this.translate.instant('Suchformulare') });
    }
  }

  toggleFilter(): void {
    if (this.showFilter) {
      this.showFilter = false;
      this.activeFilterTabIndex = 0;

      if (this.isTarget) {
        this.catalogService.selectedStatesTarget = [];
      } else {
        this.catalogService.selectedStates = [];
      }
      this.dataSource = this.lazyDataSource;

      this.onStateChanged.emit({ mode: '', states: [] });
    } else {
      this.showFilter = true;
    }
  }

  onFilterTabChanged(index: number): void {
    this.activeFilterTabIndex = index;
  }
  onSearchTextChanged(value: string): void {
    this.searchText = value;
    this.searchTextChange.emit(value);
  }

  onSelectedRowKeysChanged(keys: any[]): void {
    this.selectedRowKeys = keys;
    this.selectedRowKeysChange.emit(keys);
  }

  onLanguageChanged(value: string): void {
    this.selectedTranslateLanguage = value;
    this.selectedTranslateLanguageChange.emit(value);
  }

  contextMenuShowing(e: any, itemObj: any): void {
    this.onContextMenuShowing.emit({ event: e, item: itemObj });
  }

  contextMenuItemClick(e: any, itemObj: any): void {
    this.onContextMenuItemClick.emit({ event: e, item: itemObj });
  }
}
